// The private site's access state, as a context (registration step 33).
// Tiny on purpose (no heavy imports) so the chrome and data hooks can read it
// without import cycles. SiteAccessProvider (mounted by SiteTemplateHost on
// the public routes only) supplies the value; everywhere else (/admin, the
// standalone pages, the workbench, hook tests) the default is `open`, which
// changes nothing.

import { createContext, useContext } from 'react'
import type { SiteRefusal } from '@/services/publicAccess'

export type SiteAccessNotice = 'sessionEnded' | 'notMember' | null

export type SiteAccess = (
  | { kind: 'open' }
  | { kind: 'checking' }
  | { kind: 'admitted' }
  | { kind: 'walled'; reason: SiteRefusal; notice: SiteAccessNotice }
  | { kind: 'unavailable' }
) & { retry: () => void }

const noop = () => {}

/** A public league (or anywhere outside the provider): one frozen object. */
export const SITE_ACCESS_OPEN: SiteAccess = Object.freeze({ kind: 'open', retry: noop }) as SiteAccess

export const SiteAccessContext = createContext<SiteAccess>(SITE_ACCESS_OPEN)

export function useSiteAccess(): SiteAccess {
  return useContext(SiteAccessContext)
}

/** Whether the visitor may see league content (open or admitted). */
export function isContentAllowed(access: SiteAccess): boolean {
  return access.kind === 'open' || access.kind === 'admitted'
}

/** Gated routes render the wall in place (checking, walled or unavailable). */
export function isWalled(access: SiteAccess): boolean {
  return access.kind === 'checking' || access.kind === 'walled' || access.kind === 'unavailable'
}

/**
 * For chrome and data hooks: may they fetch league content? A pure context
 * read (no state, no effect), so a hook's effect order holds; always true on
 * a public league.
 */
export function useSiteContentAllowed(): boolean {
  return isContentAllowed(useContext(SiteAccessContext))
}
